import { supabase } from '../supabase';
import { getState } from '../userStore';

/**
 * Send a message to the ClearPath AI assistant.
 * @param {Array<{role: 'user'|'assistant', content: string}>} messages - Full conversation history
 * @returns {Promise<string>} The assistant's reply
 */
export async function sendMessage(messages) {
  const { carrier, planType, firstName, zipCode, age, gender, medications } = getState();
  const userContext = {
    carrier, planType, firstName, zipCode, age, gender,
    medications: (medications || []).map(m => ({
      medication_name: m.medication_name,
      dosage:          m.dosage    || null,
      frequency:       m.frequency || null,
    })),
  };

  const { data, error } = await supabase.functions.invoke('ai-chat', {
    body: { messages, userContext },
  });

  if (error) throw new Error(error.message || 'AI request failed');
  if (!data?.content) throw new Error('Empty response from AI');
  return data.content;
}

/**
 * Interpret a natural language search query into structured FindCare filter params.
 * @param {string} query - What the user typed, e.g. "I need a cardiologist who takes BCBS"
 * @param {string|null} icd10 - Optional ICD-10 code from triage result for routing hint
 * @returns {Promise<{filter, specialtyMatches, contextBanner, telehealth, pediatric, canFulfill, caveat}>}
 */
export async function interpretSearch(query, icd10 = null) {
  const { carrier, planType, firstName, zipCode } = getState();
  const userContext = { carrier, planType, firstName, zipCode };
  if (icd10) userContext.icd10 = icd10;

  const { data, error } = await supabase.functions.invoke('ai-chat', {
    body: { mode: 'search', query, userContext },
  });

  if (error) throw new Error(error.message || 'Search interpretation failed');
  return data;
}

/**
 * Parse free-text symptom input into structured intake data.
 * Returns the best matching protocol, person type, age hint, and any
 * answers that can be confidently prefilled from the text.
 *
 * @param {string} text - What the user typed on the intake screen
 * @returns {Promise<{
 *   symptomCluster: string|null,
 *   personType: 'self'|'child'|'other',
 *   ageHint: number|null,
 *   prefilledAnswers: object
 * }>}
 */
export async function parseIntake(text) {
  const { data, error } = await supabase.functions.invoke('ai-chat', {
    body: { mode: 'intake', text },
  });

  if (error) {
    console.warn('[parseIntake] AI call failed:', error.message);
    return { symptomCluster: null, personType: 'self', ageHint: null, prefilledAnswers: {} };
  }

  return {
    symptomCluster: data?.symptomCluster ?? null,
    personType: data?.personType ?? 'self',
    ageHint: data?.ageHint ?? null,
    prefilledAnswers: data?.prefilledAnswers ?? {},
  };
}
